import type { ReactNode } from "react";
import { SlideHeader } from "../../../SlideHeader";

export default function UnitCostModel() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:pl-24 md:pr-20 md:pt-16 md:pb-10 md:[&_header]:mb-6">
      <SlideHeader unnumbered eyebrow="Appendix I · Unit-Cost Model">
        <h1 className="text-2xl font-semibold leading-tight tracking-tight md:text-4xl md:leading-[1.15]">
          ~€2.44 direct cost per customer per month — the build-up behind the
          margin claim.
        </h1>
        <p className="mt-3 text-base text-slate-600 md:mt-4 md:text-lg">
          Live per-audit telemetry, blended across the m12 tier mix. Against
          €184 blended ARPU: ~99% gross margin on direct audit cost today;
          the 85% steady-state target holds room for Generate + full Localize
          as those engines mature.
        </p>
      </SlideHeader>

      <div className="grid grid-cols-1 gap-5 md:flex-1 md:grid-cols-2 md:gap-8">
        {/* Cost build-up */}
        <Card label="Cost build-up (per month)">
          <table className="mt-3 w-full border-collapse text-xs md:text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-[10px] font-semibold uppercase tracking-wide text-slate-500 md:text-xs">
                <th className="pb-1.5 text-left">Level</th>
                <th className="pb-1.5 text-right">Math</th>
                <th className="pb-1.5 text-right">Cost/mo</th>
              </tr>
            </thead>
            <tbody className="text-slate-800">
              <CostRow
                level="Full cache-skip audit"
                math="~€0.09 × 4.33 runs"
                cost="€0.39"
              />
              <CostRow
                level="Cached daily audit"
                math="~€0.01 × ~26 runs"
                cost="€0.26"
              />
              <CostRow
                level="Per project-market-store"
                math="sum"
                cost="€0.65"
                emphasized
              />
              <CostRow
                level="Per project-market"
                math="× 1.5 stores avg"
                cost="€0.98"
              />
              <CostRow
                level="Per project"
                math="× 1.25 markets avg"
                cost="€1.22"
              />
              <CostRow
                level="Per customer"
                math="× 2 projects avg"
                cost="~€2.44"
                emphasized
              />
            </tbody>
          </table>
          <p className="mt-3 text-xs leading-relaxed text-slate-500">
            Source: per-audit telemetry — Firestore{" "}
            <code className="text-[11px]">AsoAudit.execution.cost</code> +
            BigQuery infra-cost view, n=150 audits sampled 2026-06-11.
          </p>
        </Card>

        <div className="flex flex-col gap-5 md:gap-6">
          {/* Assumptions */}
          <Card label="Blended assumptions (m12 tier mix)">
            <ul className="mt-3 space-y-1.5 text-xs leading-snug text-slate-700 md:text-sm">
              <Bullet>
                <strong className="font-semibold text-slate-900">
                  2 projects per customer
                </strong>{" "}
                — Free/Basic capped at 1; Pro/Elite/Enterprise average higher
                within caps.
              </Bullet>
              <Bullet>
                <strong className="font-semibold text-slate-900">
                  1.25 markets per project
                </strong>{" "}
                — most run a home market; Elite/Enterprise lift the average.
              </Bullet>
              <Bullet>
                <strong className="font-semibold text-slate-900">
                  1.5 stores per project
                </strong>{" "}
                — min 1, max 2; most projects cover both stores.
              </Bullet>
              <Bullet>
                <strong className="font-semibold text-slate-900">
                  Cadence per project-market-store
                </strong>{" "}
                — 1 full cache-skip audit + 6 cached audits per week.
              </Bullet>
            </ul>
          </Card>

          {/* Fixed opex */}
          <Card label="Monthly fixed opex (live)">
            <p className="mt-3 text-2xl font-bold tracking-tight text-blue-700 md:text-3xl">
              ~€1,450/mo
            </p>
            <p className="mt-1 text-xs leading-relaxed text-slate-600 md:text-sm">
              Averaged 2026-03 → 2026-05 from the ledger. Top lines: Anthropic
              (€300), Firebase (€220), Codespaces (€180), Workspace (€85),
              Decodo (€70). Independent of customer count at current scale.
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Card({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col rounded-lg bg-slate-50 p-4 ring-1 ring-slate-200 md:p-5">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">
        {label}
      </p>
      {children}
    </div>
  );
}

function CostRow({
  level,
  math,
  cost,
  emphasized,
}: {
  level: string;
  math: string;
  cost: string;
  emphasized?: boolean;
}) {
  return (
    <tr
      className={`border-b border-slate-100 last:border-b-0 ${
        emphasized ? "font-semibold text-slate-900" : ""
      }`}
    >
      <td className="py-1.5">{level}</td>
      <td className="py-1.5 text-right text-slate-500">{math}</td>
      <td className="py-1.5 text-right tabular-nums">{cost}</td>
    </tr>
  );
}

function Bullet({ children }: { children: ReactNode }) {
  return (
    <li className="flex gap-2">
      <span aria-hidden="true" className="text-blue-600">
        •
      </span>
      <span>{children}</span>
    </li>
  );
}
